import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import { Panel } from 'foundations-components/transitional/components/Panel';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SettingsSection from 'automation-ui-canvas/containers/settings/v2/SettingsSection';
import { getClassicSettings, getPlatformSettings } from 'automation-ui-canvas/containers/settings/v2/SettingsConfigurations';
import { compose } from 'redux';
import { defaultErrorHandlingForUpdateFlow, wireUpdateFlow } from 'automation-ui-canvas/queries/flowQueries';
import { useGetFeaturePermission } from 'automation-ui-permissions/feature-permissions/hooks/featurePermissionHooks';
import { EDIT_SETTINGS } from 'automation-ui-permissions/feature-permissions/constants/WorkflowManagementFeatures';
import * as UsageTrackingScreens from 'automation-ui-usage-tracking/UsageTrackingScreens';
import classicSettingsTranslations from 'i2l?query=sporks!automation-ui-canvas/lang/settings/en.lyaml';
import LazyLangLoaderDecorator from 'automation-ui-i18n/LazyLangLoaderDecorator';
import { getClassicEnrollmentSettings, getIsClassicWorkflow, getWorkflowId } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import * as SourceApps from 'automation-ui-source-apps/constants/SourceApps';
import { useDispatch } from 'react-redux';
import useSettingsModelState from 'automation-ui-canvas/containers/settings/v2/useSettingsModelState';
import * as BlockedDateType from 'automation-ui-canvas/flow/constants/BlockedDateType';
import { trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import TimeWindowMetadata from 'automation-ui-canvas/flow/records/properties/Timing/TimeWindowMetadata';
import { updateCampaignAssociations } from 'automation-ui-canvas/data/api/campaignAssociationApi';
import { useMutation, useQuery } from 'data-fetching-client';
import { GET_SFDC_PORTAL_SETTINGS_QUERY } from 'automation-ui-canvas/queries/SalesforceEnabledPortalSettingQuery';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import * as AlertTypes from 'automation-ui-canvas/constants/AlertTypes';
import { subscriptionSettingsFormToRequestAdapter, subscriptionSettingsRequestToFormAdapter } from 'automation-ui-canvas/containers/settings/WorkflowNotificationSettingsDecorator';
import { GET_WORFKLOW_NOTIFICATIONS_SUBSCRIPTIONS_SETTINGS, UPDATE_WORKFLOW_NOTIFICATIONS_SUBSCRIPTIONS_SETTINGS } from 'automation-ui-canvas/queries/notificationSettingsQueries';
import { addAlert } from 'automation-ui-canvas/actions/MessageActions';
import { ScreenTrackingContextProvider } from 'automation-ui-usage-tracking/ScreenTrackingContext';
import TrackPageView from 'automation-ui-usage-tracking/TrackPageView';
import { EXPANDED } from 'automation-ui-canvas/constants/settings/SettingsParams';
import { Button } from 'foundations-components/transitional/components/Button';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UICloseButton from 'UIComponents/button/UICloseButton';
import wireFlow from 'automation-ui-canvas/decorators/WiredFlowDecorator';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import { PLATFORM_NOTIFICATION_SETTINGS } from 'automation-ui-usage-tracking/UsageTrackingSubscreens';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { Map as ImmutableMap } from 'immutable';
import { REDIRECT_TO_PANEL } from 'automation-ui-canvas/components/flow-header/menu-bar/MenuBar';
import { getEnabledSettingsForTracking } from 'automation-ui-canvas/containers/settings/utils/notificationSettingsTracking';
import { useFlpAccess } from 'automation-ui-permissions/flp/hooks/useFlpAccess';
import { UPDATE_ENROLLMENT_CONVERSION_STATUS } from 'automation-ui-canvas/containers/enrollment-conversion/panel/EnrollmentConversionSidePanel';
import { FETCH_ENROLLMENT_CONVERSION_STATUS } from 'automation-ui-canvas/containers/enrollment-conversion/wireGetEnrollmentConversionTrackingStatus';
import { useIsUngated } from 'automation-ui-core/hooks/authHooks';
import { METRICS_LIMITS } from 'automation-ui-canvas/constants/Gates';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SettingsSidebar = ({
  match,
  location,
  history,
  areLangStringsLoading,
  updateFlow,
  onClose,
  flow,
  setIsDirty
}) => {
  const hasRedirect = !!new URLSearchParams(history.location.search).get(REDIRECT_TO_PANEL);
  const dispatch = useDispatch();
  const [savePending, setSavePending] = useState(false);
  const isUngatedForMetricsToggle = useIsUngated(METRICS_LIMITS);
  const {
    data: salesforcePortalSetting
  } = useQuery(GET_SFDC_PORTAL_SETTINGS_QUERY);
  const metricsToggleQueryResult = useQuery(FETCH_ENROLLMENT_CONVERSION_STATUS, {
    variables: {
      flowId: flow.flowId
    },
    skip: !isUngatedForMetricsToggle,
    fetchPolicy: 'network-only'
  });
  const notificationsQueryResult = useQuery(GET_WORFKLOW_NOTIFICATIONS_SUBSCRIPTIONS_SETTINGS, {
    variables: {
      flowId: match.params.flowId
    }
  });
  const [updateNotifications] = useMutation(UPDATE_WORKFLOW_NOTIFICATIONS_SUBSCRIPTIONS_SETTINGS, {
    onCompleted: response => {
      const {
        subscribedUserIds,
        subscribedTeamIds,
        notificationConfigs
      } = subscriptionSettingsRequestToFormAdapter(response.workflowNotificationsSubscriptionsSettings);
      const {
        ['ENROLLMENT_DIGEST']: {
          rule: {
            increaseThresholdPercentage,
            decreaseThresholdPercentage,
            notificationCadence
          }
        }
      } = notificationConfigs;
      trackEvent(Trackables.SAVED_ENROLLMENT_NOTIFICATION_SETTINGS, {
        subscreen: PLATFORM_NOTIFICATION_SETTINGS,
        hasIncreaseThresholdSet: !isNaN(increaseThresholdPercentage),
        hasDecreaseThresholdSet: !isNaN(decreaseThresholdPercentage),
        hasUsersSet: subscribedUserIds.length >= 1,
        hasTeamsSet: subscribedTeamIds.length >= 1,
        notificationCadence,
        type: getEnabledSettingsForTracking(notificationConfigs)
      });
      dispatch(addAlert(AlertTypes.WORKFLOW_NOTIFICATIONS_SETTINGS_SAVING_SUCCESS));
    },
    onError: () => {
      dispatch(addAlert(AlertTypes.WORKFLOW_NOTIFICATIONS_SETTINGS_SAVING_FAIL));
    },
    refetchQueries: [GET_WORFKLOW_NOTIFICATIONS_SUBSCRIPTIONS_SETTINGS]
  });
  const [updateMetricsToggle] = useMutation(UPDATE_ENROLLMENT_CONVERSION_STATUS, {
    onError: () => {
      dispatch(addAlert(AlertTypes.WORKFLOW_METRICS_TOGGLE_SETTINGS_SAVING_FAIL));
    },
    refetchQueries: [FETCH_ENROLLMENT_CONVERSION_STATUS]
  });
  const refsById = useRef(ImmutableMap());
  const scrollTo = useCallback(id => {
    const moduleRef = refsById.current.get(id);
    if (moduleRef) {
      moduleRef.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, []);
  const settingsConfiguration = flow && Boolean(getIsClassicWorkflow(flow)) ? getClassicSettings() : getPlatformSettings();
  const classicEnrollmentSettings = useSettingsModelState({
    flow,
    initialize: initialFlow => {
      return getClassicEnrollmentSettings(initialFlow);
    }
  });
  const workflowId = getWorkflowId(flow);
  const timeWindow = useSettingsModelState({
    flow,
    initialize: initialFlow => {
      return initialFlow && initialFlow.getTimeWindow() || new TimeWindowMetadata();
    }
  });
  const campaigns = useSettingsModelState({
    flow,
    initialize: () => {
      /* initialized by campaigns-lib components */
      return [];
    }
  });
  const scheduledDisableAt = useSettingsModelState({
    flow,
    initialize: initialFlow => {
      return {
        value: initialFlow && initialFlow.getScheduledDisableAt() || null
      };
    }
  });
  const metricsToggle = useSettingsModelState({
    flow,
    dataQuery: metricsToggleQueryResult,
    initialize: (_, queryResult) => {
      var _queryResult$data$fet, _queryResult$data;
      return {
        value: (_queryResult$data$fet = (_queryResult$data = queryResult.data) === null || _queryResult$data === void 0 || (_queryResult$data = _queryResult$data.fetchEnrollmentConversionStatus) === null || _queryResult$data === void 0 ? void 0 : _queryResult$data.conversionReportingEnabled) !== null && _queryResult$data$fet !== void 0 ? _queryResult$data$fet : false
      };
    }
  });
  const notifications = useSettingsModelState({
    flow,
    dataQuery: notificationsQueryResult,
    initialize: (_, queryResult) => {
      return subscriptionSettingsRequestToFormAdapter(queryResult.data.workflowNotificationsSubscriptionsSettings);
    },
    getDisabledTooltipMessage: notificationSettings => {
      if (notificationSettings && (notificationSettings.notificationConfigs.ENROLLMENT_DIGEST.isEnabled || notificationSettings.notificationConfigs.ISSUE_NOTIFICATION.isEnabled || notificationSettings.notificationConfigs.AI_BASED_ENROLLMENT_DIGEST.isEnabled) && notificationSettings.subscribedTeamIds.length === 0 && notificationSettings.subscribedUserIds.length === 0) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "settingsV2.notificationRecipientsSetting.recipientsValidation"
        });
      }
      return null;
    }
  });
  const getFeaturePermission = useGetFeaturePermission();
  const hasSettingsChanged = useMemo(() => timeWindow.hasChanged || campaigns.hasChanged || classicEnrollmentSettings.hasChanged || notifications.hasChanged || scheduledDisableAt.hasChanged || metricsToggle.hasChanged, [timeWindow, campaigns, classicEnrollmentSettings, notifications, scheduledDisableAt, metricsToggle]);
  const disabledTooltipMessage = useMemo(() => timeWindow.disabledTooltipMessage || campaigns.disabledTooltipMessage || classicEnrollmentSettings.disabledTooltipMessage || notifications.disabledTooltipMessage || scheduledDisableAt.disabledTooltipMessage, [timeWindow, campaigns, classicEnrollmentSettings, notifications, scheduledDisableAt]);
  useEffect(() => {
    setIsDirty(hasSettingsChanged);
  }, [hasSettingsChanged, setIsDirty]);

  /* prevent refreshes and back navigation when changes are pending */
  const handleBeforeUnloadEvent = useCallback(event => {
    if (hasSettingsChanged) {
      event.preventDefault();
      event.returnValue = '';
    }
  }, [hasSettingsChanged]);
  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnloadEvent);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnloadEvent);
    };
  });
  const handleClosePanel = useCallback(() => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete(EXPANDED);
    onClose({
      search: newSearchParams.toString()
    });
  }, [location, onClose]);
  const handleOnCancelClick = useCallback(() => {
    handleClosePanel();
  }, [handleClosePanel]);
  const {
    data: flpAccessData,
    error: flpAccessError
  } = useFlpAccess(flow.flowId || undefined);
  const hasFlpWriteAccess = !flpAccessError && (flpAccessData === null || flpAccessData === void 0 ? void 0 : flpAccessData.hasAccess(EDIT_SETTINGS));
  const {
    hasFeatureAccess: hasEditSettingsAccess
  } = getFeaturePermission({
    feature: EDIT_SETTINGS
  });
  const isRevisionPreview = !isNaN(match.params.revisionId);
  const isReadOnly = !hasEditSettingsAccess || isRevisionPreview || !hasFlpWriteAccess;
  const handleSaveSettings = () => {
    let updatedFlow = flow;
    setSavePending(true);
    if (updatedFlow && scheduledDisableAt.hasChanged) {
      updatedFlow = updatedFlow.setScheduledDisableAt(scheduledDisableAt.setting ? scheduledDisableAt.setting.value : null);
    }
    if (updatedFlow && classicEnrollmentSettings.hasChanged && classicEnrollmentSettings.setting) {
      updatedFlow = updatedFlow.setClassicEnrollmentSettings(classicEnrollmentSettings.setting);
    }
    if (updatedFlow && timeWindow.hasChanged && timeWindow.setting) {
      const quickOptionDays = timeWindow.setting.getQuickOptionDays();
      const customDate = !timeWindow.setting.getBlockedDates().isEmpty();
      const recurringDate = timeWindow.setting.getBlockedDates().some(blockedDate => blockedDate.getType() === BlockedDateType.RECURRING);
      trackEvent(Trackables.SAVED_EXECUTION_TIME, {
        quickOptionDays,
        customDate,
        recurringDate
      });
      const timeWindowMetadata = timeWindow.setting.requestCompatible();
      updatedFlow = updatedFlow.setTimeWindowMetadata(timeWindowMetadata);
    }
    if (updatedFlow) {
      updatedFlow = updatedFlow.removeAction(SpecialActionIds.NEW_ACTION);
      const saveAllPromise = new Promise((resolve, reject) => {
        updateFlow({
          sourceApp: SourceApps.WORKFLOWS_APP,
          flow: updatedFlow
        }).then(latestFlow => {
          const promises = [];
          if (updatedFlow && campaigns.hasChanged && campaigns.setting) {
            const saveCampaigns = updateCampaignAssociations(workflowId, campaigns.setting);
            promises.push(saveCampaigns);
          }
          if (updatedFlow && notifications.hasChanged && notifications.setting) {
            const saveNotifications = updateNotifications({
              variables: Object.assign({
                flowId: updatedFlow.flowId,
                outdatedIfLastUpdatedMoreRecentThan: notificationsQueryResult.data && notificationsQueryResult.data.workflowNotificationsSubscriptionsSettings.lastUpdatedAt || 0
              }, subscriptionSettingsFormToRequestAdapter(notifications.setting))
            });
            promises.push(saveNotifications);
          }
          if (updatedFlow && metricsToggle.hasChanged && metricsToggle.setting) {
            const saveMetricsToggle = updateMetricsToggle({
              variables: {
                flowId: updatedFlow.flowId,
                conversionReportingEnabled: metricsToggle.setting.value
              }
            });
            promises.push(saveMetricsToggle);
          }
          Promise.all(promises).then(() => {
            resolve(latestFlow);
          }).catch(error => reject(error));
        }).catch(error => reject(error));
      });
      saveAllPromise.then(() => {
        classicEnrollmentSettings.onCommitChange();
        campaigns.onCommitChange();
        timeWindow.onCommitChange();
        notifications.onCommitChange();
        scheduledDisableAt.onCommitChange();
        metricsToggle.onCommitChange();
      }).catch(error => {
        defaultErrorHandlingForUpdateFlow({
          error,
          dispatch
        });
      }).finally(() => {
        setSavePending(false);
      });
    }
  };
  const onSettingChange = useCallback(({
    classicEnrollmentSettings: updatedClassicEnrollmentSettings,
    timeWindow: updatedTimeWindow,
    campaigns: updatedCampaigns,
    notifications: updatedNotifications,
    scheduledDisableAt: updatedScheduledDisableAt,
    metricsToggle: updatedMetricsToggle
  }, options) => {
    classicEnrollmentSettings.setSetting(updatedClassicEnrollmentSettings, options);
    timeWindow.setSetting(updatedTimeWindow, options);
    campaigns.setSetting(updatedCampaigns, options);
    notifications.setSetting(updatedNotifications, options);
    scheduledDisableAt.setSetting(updatedScheduledDisableAt, options);
    metricsToggle.setSetting(updatedMetricsToggle, options);
  }, [classicEnrollmentSettings, timeWindow, campaigns, notifications, scheduledDisableAt, metricsToggle]);
  return flow ? /*#__PURE__*/_jsx(NavMarker, {
    name: "SETTINGS_SIDEBAR",
    children: /*#__PURE__*/_jsxs(ScreenTrackingContextProvider, {
      value: UsageTrackingScreens.PLATFORM_SETTINGS,
      children: [/*#__PURE__*/_jsx(TrackPageView, {
        extraJSON: {
          userReadOnly: isReadOnly
        }
      }), /*#__PURE__*/_jsxs(Panel, {
        "data-test-id": "settings-sidebar",
        width: 500,
        children: [/*#__PURE__*/_jsxs(PanelHeader, {
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h2",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.common.settingsButton"
            })
          }), /*#__PURE__*/_jsx(UICloseButton, {
            onClick: handleOnCancelClick
          })]
        }), /*#__PURE__*/_jsx(PanelBody, {
          paddingBlock: "400",
          children: flow && !areLangStringsLoading && settingsConfiguration.map(({
            title,
            modules,
            id
          }, index) => /*#__PURE__*/_jsx(SettingsSection, {
            location: location,
            salesforcePortalSetting: salesforcePortalSetting && salesforcePortalSetting.portalSettings,
            history: history,
            flow: flow,
            settings: {
              classicEnrollmentSettings: classicEnrollmentSettings.setting,
              timeWindow: timeWindow.setting,
              campaigns: campaigns.setting,
              notifications: notifications.setting,
              scheduledDisableAt: scheduledDisableAt.setting,
              metricsToggle: metricsToggle.setting
            },
            onChange: onSettingChange,
            readOnly: isReadOnly,
            id: id,
            title: title,
            modules: modules,
            hasBorder: index !== 0,
            defaultOpen: index === 0,
            use: 'default',
            scrollTo: scrollTo,
            refsById: refsById
          }, id))
        }), /*#__PURE__*/_jsxs(PanelFooter, {
          align: "left",
          children: [/*#__PURE__*/_jsx(UITooltip, {
            title: disabledTooltipMessage,
            disabled: !disabledTooltipMessage,
            children: /*#__PURE__*/_jsx(UILoadingButton, {
              use: "primary",
              onClick: handleSaveSettings,
              onLoadingFinish: handleClosePanel,
              disabled: !hasSettingsChanged || isReadOnly || !!disabledTooltipMessage,
              loading: savePending,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation.common.save"
              })
            })
          }), /*#__PURE__*/_jsx(Button, {
            use: "secondary",
            onClick: handleOnCancelClick,
            children: hasRedirect ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.common.back"
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.common.cancel"
            })
          })]
        })]
      })]
    })
  }) : null;
};
export default compose(withRouter, wireAuthConsumer, wireUpdateFlow, LazyLangLoaderDecorator([classicSettingsTranslations]), wireFlow)(SettingsSidebar);